import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { brokersAPI, newsAPI, calendarAPI, tradersAPI, toolboxAPI } from '../lib/api';
import { trackVPSClick, trackSearch } from '../lib/analytics';
import { formatDate, formatPercentage, getCurrencyFlag, generateBrokerScore, generateTrustScore } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { SEO, brokerPath } from '../components/SEO';
import { 
  Search, ArrowRight, TrendingUp, TrendingDown, Shield, Calendar, Newspaper, Users, Building2,
  Globe, Zap, Award, ChevronRight, Star, Server, Bot, LineChart, Flame, Crown, Sparkles, 
  CheckCircle, AlertTriangle, Eye, MessageSquare, BarChart3, Activity, Clock, ChevronDown,
  ArrowUpRight, ArrowDownRight, Target, Percent, DollarSign, FileWarning, ExternalLink
} from 'lucide-react';
import { languageCodes, defaultLanguage } from '../i18n';
import SparksTray from '../components/SparksTray';

// VPS Affiliate Link - Ultra-Fast Forex VPS
const VPS_AFFILIATE_LINK = "https://secure.fxsvps.com/aff.php?aff=597";

const BRAND = {
  purple: '#5444e5', purpleLight: '#6b5ce7', purpleMid: '#703ce7', purpleDark: '#4438b8',
  purpleAccent: '#8537e9', purpleSoft: '#a35ded', purpleBg: '#f3f1fe', purpleBgDark: '#e8e5fc',
  orange: '#f97316', orangeLight: '#fb923c', orangeDark: '#ea580c',
  red: '#ef4444', redOrange: '#f43f5e', green: '#22c55e', greenDark: '#16a34a',
};

// Simulated market data
const MARKET_DATA = [
  { symbol: 'EUR/USD', price: 1.0847, change: 0.23, bid: 1.0846, ask: 1.0848 },
  { symbol: 'GBP/USD', price: 1.2634, change: -0.15, bid: 1.2633, ask: 1.2635 },
  { symbol: 'USD/JPY', price: 154.82, change: 0.42, bid: 154.81, ask: 154.83 },
  { symbol: 'XAU/USD', price: 2648.50, change: 1.12, bid: 2648.30, ask: 2648.70 },
  { symbol: 'BTC/USD', price: 98542, change: 2.34, bid: 98540, ask: 98544 },
  { symbol: 'EUR/GBP', price: 0.8586, change: -0.08, bid: 0.8585, ask: 0.8587 },
  { symbol: 'AUD/USD', price: 0.6298, change: 0.31, bid: 0.6297, ask: 0.6299 },
  { symbol: 'USD/CHF', price: 0.8912, change: -0.19, bid: 0.8911, ask: 0.8913 },
];

// Fallback sentiment data (used if API fails)
const DEFAULT_SENTIMENT_DATA = [
  { pair: 'EUR/USD', long: 42, short: 58 },
  { pair: 'GBP/USD', long: 55, short: 45 },
  { pair: 'XAU/USD', long: 68, short: 32 },
  { pair: 'USD/JPY', long: 38, short: 62 },
];

// Ranking filter types
const RANKING_FILTERS = [
  { key: 'awards', icon: 'Award', label: 'Awards' },
  { key: 'licenses', icon: 'Shield', label: 'Licenses' },
  { key: 'growth', icon: 'TrendingUp', label: 'Growth' },
  { key: 'comments', icon: 'MessageSquare', label: 'Comments' },
  { key: 'likes', icon: 'Star', label: 'Likes' },
];

export default function HomePage() {
  const { isDarkish: isDark } = useTheme();
  const { t, i18n } = useTranslation();
  // Phase 1: Critical above-the-fold data
  const [brokers, setBrokers] = useState([]);
  const [allBrokers, setAllBrokers] = useState([]);
  const [brokerStats, setBrokerStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Phase 2: Deferred below-the-fold data
  const [news, setNews] = useState([]);
  const [liveNews, setLiveNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [traders, setTraders] = useState([]);
  const [tools, setTools] = useState([]);
  const [deferredLoading, setDeferredLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [animatedStats, setAnimatedStats] = useState({ brokers: 0, regulators: 0, traders: 0, reviews: 0 });
  const [activeRankingFilter, setActiveRankingFilter] = useState('awards');
  const [sentimentData, setSentimentData] = useState(DEFAULT_SENTIMENT_DATA);
  const statsRef = useRef(null);

  // Get localized path helper
  const getLocalizedPath = (path) => {
    if (i18n.language === defaultLanguage) {
      return path;
    }
    return `/${i18n.language}${path === '/' ? '' : path}`;
  };

  // Phase 1: Load critical above-the-fold data FAST
  useEffect(() => {
    const loadCritical = async () => {
      try {
        const [brokersRes, allBrokersRes, brokerStatsRes] = await Promise.all([
          brokersAPI.getFeatured(),
          brokersAPI.getAll({ limit: 20 }),
          brokersAPI.getStatsSummary(),
        ]);
        setBrokers(brokersRes.data || []);
        setAllBrokers(allBrokersRes.data || []);
        setBrokerStats(brokerStatsRes.data || null);
      } catch (error) {
        console.error('Error loading critical data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCritical();
  }, []);

  // Phase 2: Load deferred below-the-fold data after critical paint
  useEffect(() => {
    if (loading) return; // Wait for Phase 1 to finish
    const loadDeferred = async () => {
      try {
        const [newsRes, eventsRes, tradersRes, toolsRes, liveNewsRes, sentimentRes] = await Promise.allSettled([
          newsAPI.getAll({ limit: 10 }),
          calendarAPI.getEvents({ limit: 8 }),
          tradersAPI.getAll({ limit: 5 }),
          toolboxAPI.getAll({ limit: 8 }),
          newsAPI.getCombinedNews('all', 'us', 10),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/sentiment`).then(r => r.ok ? r.json() : null),
        ]);
        if (newsRes.status === 'fulfilled') setNews(newsRes.value?.data || []);
        if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value?.data?.slice(0, 6) || []);
        if (tradersRes.status === 'fulfilled') setTraders(tradersRes.value?.data?.slice(0, 5) || []);
        if (toolsRes.status === 'fulfilled') setTools(toolsRes.value?.data || []);
        if (liveNewsRes.status === 'fulfilled' && liveNewsRes.value?.data?.news?.length > 0) {
          setLiveNews(liveNewsRes.value.data.news);
        }
        if (sentimentRes.status === 'fulfilled' && sentimentRes.value?.data?.length > 0) {
          setSentimentData(sentimentRes.value.data);
        }
      } catch (error) {
        console.error('Error loading deferred data:', error);
      } finally {
        setDeferredLoading(false);
      }
    };
    loadDeferred();
  }, [loading]);

  // Animate stats when brokerStats loads
  useEffect(() => {
    if (brokerStats) {
      const targets = { 
        brokers: brokerStats.total_brokers || 5491, 
        regulators: brokerStats.top_regulators?.length || 63, 
        traders: 500,
        reviews: 52000 
      };
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setAnimatedStats({
          brokers: Math.floor(targets.brokers * progress),
          regulators: Math.floor(targets.regulators * progress),
          traders: Math.floor(targets.traders * progress),
          reviews: Math.floor(targets.reviews * progress),
        });
        if (step >= steps) clearInterval(timer);
      }, interval);
      return () => clearInterval(timer);
    }
  }, [brokerStats]);

  const uniqueBrokers = allBrokers.filter((broker, index, self) => 
    index === self.findIndex(b => b.name === broker.name)
  ).slice(0, 10);

  // Featured brokers with logos for the top banner
  const bannerBrokers = brokers.filter(b => b.logo_url).slice(0, 7);
  
  // Filter and sort brokers based on active filter
  const getFilteredBrokers = () => {
    let filtered = [...uniqueBrokers];
    
    switch (activeRankingFilter) {
      case 'awards':
        // Sort by generated trust score (proxy for awards)
        filtered.sort((a, b) => generateTrustScore(b.id) - generateTrustScore(a.id));
        break;
      case 'licenses':
        // Sort by number of regulations
        filtered.sort((a, b) => (b.regulations?.length || 0) - (a.regulations?.length || 0));
        break;
      case 'growth':
        // Sort by founded year (newer = more growth potential)
        filtered.sort((a, b) => (b.founded_year || 2000) - (a.founded_year || 2000));
        break;
      case 'comments':
        // Sort by total reviews
        filtered.sort((a, b) => (b.total_reviews || 0) - (a.total_reviews || 0));
        break;
      case 'likes':
        // Sort by generated broker score
        filtered.sort((a, b) => generateBrokerScore(b.id) - generateBrokerScore(a.id));
        break;
      default:
        break;
    }
    
    return filtered.slice(0, 10);
  };
  
  const filteredBrokers = getFilteredBrokers();
  
  // Get display news (prioritize live news, fallback to DB news)
  const displayNews = liveNews.length > 0 ? liveNews : news;

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M+';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K+';
    return num + '+';
  };

  return (
    <div data-testid="home-page" className={cn("min-h-screen pt-14", isDark ? "bg-[#0a0a0f]" : "bg-gray-50")}>
      {/* SEO Meta Tags */}
      <SEO 
        title={t('home.hero_title', 'Find Your Perfect Forex Broker') + ' | FX Astra'}
        description={t('home.hero_subtitle', 'Compare 5,000+ brokers, read reviews, and make informed trading decisions')}
        keywords="forex broker comparison, best forex brokers, forex trading platform, CFD brokers, forex broker reviews, trading tools, live forex charts, economic calendar"
        canonicalUrl="/"
        ogType="website"
      />
      
      {/* SPARKS TRAY (Vibes — short-video stories under the header) */}
      <section data-testid="home-sparks-section">
        <div className="container-app py-3">
          <SparksTray currentUser={(() => { try { return JSON.parse(localStorage.getItem('fx_user') || localStorage.getItem('user') || 'null'); } catch { return null; } })()} />
        </div>
      </section>

      {/* JSON-LD Structured Data for Homepage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "FX Astra",
          "alternateName": "FXAstra",
          "url": "https://fxastra.com",
          "description": "The world's leading forex broker intelligence platform. Research 5,491+ brokers, compare spreads, read verified reviews, and trade with confidence.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://fxastra.com/brokers?search={search_term_string}",
            "query-input": "required name=search_term_string"
          },
          "publisher": {
            "@type": "Organization",
            "name": "FX Astra",
            "logo": {
              "@type": "ImageObject",
              "url": "https://fxastra.com/logo-color.png"
            }
          }
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "FX Astra",
          "url": "https://fxastra.com",
          "logo": "https://fxastra.com/logo-color.png",
          "sameAs": [],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer support",
            "availableLanguage": ["English"]
          }
        }) }}
      />
      {brokers && brokers.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Top Forex Brokers",
            "description": "Featured and top-rated forex brokers",
            "numberOfItems": brokers.length,
            "itemListElement": brokers.slice(0, 10).map((broker, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "item": {
                "@type": "FinancialService",
                "name": broker.name,
                "url": `https://fxastra.com${brokerPath(broker)}`,
                "image": broker.logo_url ? `https://fxastra.com${broker.logo_url}` : undefined,
                "aggregateRating": broker.user_rating > 0 && broker.total_reviews > 0 ? {
                  "@type": "AggregateRating",
                  "ratingValue": Number(broker.user_rating).toFixed(1),
                  "bestRating": 5,
                  "worstRating": 1,
                  "ratingCount": broker.total_reviews
                } : undefined
              }
            }))
          }) }}
        />
      )}

      {/* TOP BANNER - BROKER LOGOS (AD) */}
      <section className="py-3 sm:py-4">
        <div className="container-app">
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            {loading ? (
              [...Array(7)].map((_, idx) => (
                <div key={idx} style={{ width: 160, height: 44, flexShrink: 0 }} className={cn("rounded-md animate-pulse", isDark ? "bg-white/[0.04]" : "bg-gray-200")} />
              ))
            ) : bannerBrokers.map((broker) => (
              <Link
                key={broker.id}
                to={getLocalizedPath(brokerPath(broker))}
                className="relative group"
                style={{ flexShrink: 0 }}
              >
                <div 
                  style={{ width: 160, height: 44, flexShrink: 0 }}
                  className="rounded-md bg-white overflow-hidden opacity-90 hover:opacity-100 transition-opacity"
                >
                  {broker.logo_url ? 
                    <img src={broker.logo_url} alt={broker.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : 
                    <span className="text-gray-800 font-bold text-xs flex items-center justify-center h-full">{broker.name}</span>
                  }
                </div>
                <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[7px] font-bold px-1.5 py-0 rounded-sm leading-relaxed">AD</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH + STATS — compact polished hero */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden py-4 sm:py-5"
      >
        <div className="container-app relative">
          {/* SEO H1 (hidden) */}
          <h1 className="sr-only">
            {t('home.hero_title', 'Find Your Perfect Forex Broker')}
          </h1>

          {/* Search */}
          <div className="max-w-2xl mx-auto px-2 sm:px-0">
            <div
              className={cn(
                "group relative flex flex-col sm:flex-row rounded-2xl p-1.5 gap-1.5 sm:gap-0 border transition-all duration-200",
                "focus-within:ring-2 focus-within:ring-[#5444e5]/25 focus-within:border-[#5444e5]/40",
                isDark
                  ? "bg-white/[0.03] border-white/[0.05] shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_8px_24px_-12px_rgba(0,0,0,0.6)]"
                  : "bg-white border-gray-200 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_10px_30px_-18px_rgba(15,15,20,0.25)]"
              )}
            >
              <div className="relative flex-1">
                <div className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Search className={cn("h-4 w-4 sm:h-[18px] sm:w-[18px]", isDark ? "text-gray-500" : "text-gray-400")} />
                </div>
                <Input
                  type="text"
                  placeholder={t('home.search_placeholder', 'Search brokers, tools, or news…')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    "w-full pl-10 sm:pl-12 pr-4 sm:pr-16 h-11 sm:h-12 bg-transparent border-0 text-sm sm:text-[15px] focus-visible:ring-0",
                    isDark ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"
                  )}
                  data-testid="hero-search-input"
                />
                {/* Keyboard hint (desktop only) */}
                <kbd
                  aria-hidden
                  className={cn(
                    "hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 px-1.5 h-6 rounded-md text-[10px] font-medium border",
                    isDark
                      ? "bg-white/[0.04] text-gray-400 border-white/10"
                      : "bg-gray-50 text-gray-500 border-gray-200"
                  )}
                >
                  ⌘K
                </kbd>
              </div>
              <Button
                className={cn(
                  "h-11 sm:h-12 px-5 sm:px-7 text-white font-semibold text-sm",
                  "active:scale-[0.98] transition-colors duration-150"
                )}
                asChild
                onClick={() => searchQuery && trackSearch(searchQuery)}
                data-testid="hero-search-btn"
              >
                <Link to={getLocalizedPath(`/brokers${searchQuery ? `?search=${searchQuery}` : ''}`)}>
                  {t('common.search', 'Search')}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Link>
              </Button>
            </div>

            {/* Quick-search suggestion pills */}
            <div className="mt-4 flex flex-wrap justify-center gap-1.5 sm:gap-2">
              {[
                { label: 'ASIC Regulated', q: 'asic' },
                { label: 'FCA Regulated', q: 'fca' },
                { label: 'Low Spreads', q: 'low-spreads' },
                { label: 'MT5', q: 'mt5' },
                { label: 'ECN', q: 'ecn' },
              ].map((p) => (
                <Link
                  key={p.q}
                  to={getLocalizedPath(`/brokers?filter=${p.q}`)}
                  data-testid={`hero-quick-${p.q}`}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors",
                    isDark
                      ? "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  {p.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Stats — clean inline row (no card, no dividers) */}
          <div className="mt-6 sm:mt-7 max-w-2xl mx-auto">
            <div
              className="flex items-center justify-around sm:justify-center sm:gap-16"
              data-testid="hero-stats"
            >
              {[
                {
                  value: brokerStats?.total_brokers?.toLocaleString() || '5,491',
                  label: 'Brokers',
                },
                {
                  value: brokerStats?.top_regulators?.length || 63,
                  label: 'Regulators',
                },
                {
                  value: '52K+',
                  label: 'Reviews',
                },
              ].map((s, idx) => (
                <div key={idx} className="flex flex-col items-center text-center">
                  {loading ? (
                    <span className={cn("w-14 sm:w-16 h-6 rounded animate-pulse inline-block", isDark ? "bg-white/10" : "bg-gray-200")} />
                  ) : (
                    <span
                      className={cn(
                        "text-xl sm:text-2xl font-semibold tabular-nums tracking-tight",
                        isDark ? "text-white" : "text-gray-900"
                      )}
                    >
                      {s.value}
                    </span>
                  )}
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-[0.16em] font-medium mt-0.5",
                      isDark ? "text-gray-500" : "text-gray-500"
                    )}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BROWSING HISTORY / POPULAR BROKERS - Small Logo Row */}
      <section className="py-4">
        <div className="container-app">
          <p className={cn("text-xs sm:text-sm mb-3", isDark ? "text-gray-500" : "text-gray-600")}>Browsing History</p>
          <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {loading ? (
              // Skeleton loaders
              [...Array(10)].map((_, idx) => (
                <div key={idx} className="flex items-center gap-2 px-2 sm:px-3 py-1.5 flex-shrink-0">
                  <div className={cn("w-5 h-5 sm:w-6 sm:h-6 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                  <div className={cn("w-12 sm:w-16 h-4 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                </div>
              ))
            ) : uniqueBrokers.slice(0, 12).map((broker) => (
              <Link
                key={broker.id}
                to={brokerPath(broker)}
                className={cn(
                  "flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg transition-colors flex-shrink-0",
                  isDark ? "hover:bg-white/[0.04]" : "hover:bg-gray-100"
                )}
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded bg-white flex items-center justify-center overflow-hidden">
                  {broker.logo_url ? 
                    <img src={broker.logo_url} alt="" className="w-full h-full object-contain" /> : 
                    <Building2 className="h-3 w-3 text-gray-400" />
                  }
                </div>
                <span className={cn("text-xs sm:text-sm whitespace-nowrap", isDark ? "text-white" : "text-gray-900")}>{broker.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT - Rankings + VPS + News */}
      <section className="py-4 sm:py-6">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            
            {/* Left - Rankings List */}
            <div className="lg:col-span-4 flex flex-col">
              <Card className={cn("overflow-hidden flex-1 flex flex-col rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-yellow-400" />
                    <CardTitle className={cn("text-base", isDark ? "text-white" : "text-gray-900")}>Rankings</CardTitle>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <button 
                      data-testid="filter-awards"
                      onClick={() => setActiveRankingFilter('awards')}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                        activeRankingFilter === 'awards' 
                          ? 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/25' 
                          : isDark ? 'text-gray-500 hover:text-white hover:bg-white/[0.04]' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                      )}
                      title="Filter by Awards"
                    >
                      <Award className="h-3.5 w-3.5" />
                    </button>
                    <button 
                      data-testid="filter-licenses"
                      onClick={() => setActiveRankingFilter('licenses')}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                        activeRankingFilter === 'licenses' 
                          ? 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/25' 
                          : isDark ? 'text-gray-500 hover:text-white hover:bg-white/[0.04]' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                      )}
                      title="Filter by Licenses"
                    >
                      <Shield className="h-3.5 w-3.5" />
                    </button>
                    <button 
                      data-testid="filter-growth"
                      onClick={() => setActiveRankingFilter('growth')}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                        activeRankingFilter === 'growth' 
                          ? 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/25' 
                          : isDark ? 'text-gray-500 hover:text-white hover:bg-white/[0.04]' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                      )}
                      title="Filter by Growth"
                    >
                      <TrendingUp className="h-3.5 w-3.5" />
                    </button>
                    <button 
                      data-testid="filter-comments"
                      onClick={() => setActiveRankingFilter('comments')}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                        activeRankingFilter === 'comments' 
                          ? 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/25' 
                          : isDark ? 'text-gray-500 hover:text-white hover:bg-white/[0.04]' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                      )}
                      title="Filter by Comments"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                    <button 
                      data-testid="filter-likes"
                      onClick={() => setActiveRankingFilter('likes')}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                        activeRankingFilter === 'likes' 
                          ? 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/25' 
                          : isDark ? 'text-gray-500 hover:text-white hover:bg-white/[0.04]' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                      )}
                      title="Filter by Likes"
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 flex-1">
                  {loading ? (
                    // Skeleton loaders for rankings
                    [...Array(10)].map((_, idx) => (
                      <div key={idx} className={cn("flex items-center gap-3 p-4 border-b", isDark ? "border-white/[0.04]" : "border-gray-100")}>
                        <div className={cn("w-16 h-5 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className={cn("w-12 h-12 rounded-lg animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className="flex-1">
                          <div className={cn("w-24 h-4 rounded animate-pulse mb-2", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          <div className={cn("w-16 h-6 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        </div>
                        <div className={cn("w-8 h-8 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                      </div>
                    ))
                  ) : filteredBrokers.map((broker, idx) => (
                    <Link 
                      key={broker.id}
                      to={brokerPath(broker)}
                      data-testid={`ranking-broker-${idx}`}
                      className={cn(
                        "flex items-center gap-3 p-4 border-b transition-colors group",
                        isDark ? "border-white/[0.04] hover:bg-white/[0.03]" : "border-gray-100 hover:bg-gray-50"
                      )}
                    >
                      <div className="flex-shrink-0">
                        <Badge className={`text-[10px] ${'bg-transparent text-gray-400 border border-white/[0.08]'}`}>
                          {generateTrustScore(broker.id) >= 4 ? 'Regulated' : 'Offshore'}
                        </Badge>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden border border-gray-200">
                        {broker.logo_url ? 
                          <img src={broker.logo_url} alt="" className="w-full h-full object-contain p-1" /> : 
                          <Building2 className="h-6 w-6 text-gray-400" />
                        }
                      </div>
                      <div className="flex-1">
                        <span className={cn("font-semibold block group-hover:text-purple-400", isDark ? "text-white" : "text-gray-900")}>{broker.name}</span>
                        <span className={`text-2xl font-bold ${generateBrokerScore(broker.id) >= 4 ? 'text-green-400' : 'text-yellow-400'}`}>
                          {generateBrokerScore(broker.id).toFixed(2)}
                        </span>
                        <span className="text-gray-500 text-xs ml-1">Score</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={cn("px-2 py-0.5 rounded text-xs", isDark ? "bg-white/[0.04] text-gray-400" : "bg-gray-100 text-gray-600")}>{broker.account_types?.[0] || 'ECN Account'}</span>
                          <span className="text-gray-500 text-xs">{broker.founded_year ? `${2025 - broker.founded_year}-${2025 - broker.founded_year + 5} years` : '15-20 years'}</span>
                        </div>
                      </div>
                      <span className={`text-xl font-bold ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-orange-400' : 'text-gray-500'}`}>
                        {idx + 1}
                      </span>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            </div>
            
            {/* Middle - VPS Cards */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <a 
                  href={VPS_AFFILIATE_LINK} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="vps-standard-card"
                  className="block"
                  onClick={() => trackVPSClick('homepage_vps_standard')}
                >
                  <Card className={cn("h-full transition-colors cursor-pointer", isDark ? "bg-transparent border-white/[0.05] hover:bg-white/[0.02]" : "bg-transparent border-gray-200/70 hover:bg-gray-50")}>
                    <CardContent className="p-2 sm:p-3 text-center">
                      <div className="text-[8px] uppercase tracking-wider text-gray-500 mb-1">EA VPS</div>
                      <h4 className={cn("font-semibold text-xs sm:text-sm", isDark ? "text-white" : "text-gray-900")}>VPS Standard</h4>
                      <p className={cn("text-[8px] sm:text-[10px] hidden sm:block", isDark ? "text-gray-400" : "text-gray-600")}>1*CPU / 1G*RAM / 40G*SSD</p>
                      <p className="text-primary font-medium mt-1 flex items-center justify-center gap-1 text-[10px] sm:text-sm">
                        Get Started <ExternalLink className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      </p>
                    </CardContent>
                  </Card>
                </a>
                {uniqueBrokers.length === 0 ? (
                  // Skeleton loaders for VPS cards
                  [...Array(3)].map((_, idx) => (
                    <Card key={idx} className={cn("rounded-xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                      <CardContent className="p-2 sm:p-3 text-center">
                        <div className={cn("w-8 h-8 sm:w-10 sm:h-10 rounded-lg mx-auto mb-1 sm:mb-2 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className={cn("w-12 sm:w-16 h-3 sm:h-4 rounded mx-auto mb-1 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className={cn("w-16 sm:w-24 h-2 sm:h-3 rounded mx-auto animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                      </CardContent>
                    </Card>
                  ))
                ) : uniqueBrokers.slice(0, 3).map((broker) => (
                  <Link key={broker.id} to={brokerPath(broker)}>
                    <Card className={cn("h-full transition-colors", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                      <CardContent className="p-2 sm:p-3 text-center relative">
                        <Badge variant="outline" className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 bg-transparent text-gray-500 text-[7px] sm:text-[8px]">Free</Badge>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white mx-auto mb-1 sm:mb-2 flex items-center justify-center overflow-hidden border border-gray-200">
                          {broker.logo_url ? 
                            <img src={broker.logo_url} alt="" className="w-full h-full object-contain p-0.5 sm:p-1" /> : 
                            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                          }
                        </div>
                        <h4 className={cn("font-bold text-xs sm:text-sm truncate", isDark ? "text-white" : "text-gray-900")}>{broker.name}</h4>
                        <p className="text-gray-500 text-[8px] sm:text-[10px] hidden sm:block">2*CPU / 2G*RAM / 40G*SSD</p>
                        <p className="text-orange-400 text-[10px] sm:text-xs mt-0.5 sm:mt-1">VPS Ultra</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              
              {/* Latest News */}
              <Card className={cn("flex-1 flex flex-col rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                <CardHeader className={cn("py-2 sm:py-3 px-3 sm:px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                  <div className="flex items-center justify-between">
                    <CardTitle className={cn("text-base sm:text-lg font-bold", isDark ? "text-white" : "text-gray-900")}>Latest</CardTitle>
                    <div className="hidden sm:flex gap-4 text-sm">
                      <span className="text-purple-400">Original</span>
                      <span className="text-gray-500">Industry</span>
                      <span className="text-gray-500">Broker</span>
                      <span className="text-gray-500">Exposure</span>
                    </div>
                    <Link to="/news" className="text-purple-400 text-xs sm:text-sm">More &gt;</Link>
                  </div>
                </CardHeader>
                <CardContent className="p-4 flex-1 flex flex-col">
                  {deferredLoading ? (
                    // Skeleton loaders for news
                    [...Array(6)].map((_, idx) => (
                      <div key={idx} className="flex gap-4 mb-4">
                        <div className={cn("w-32 h-20 rounded-lg flex-shrink-0 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className="flex-1">
                          <div className={cn("w-full h-4 rounded mb-2 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          <div className={cn("w-3/4 h-4 rounded mb-2 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          <div className={cn("w-20 h-3 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        </div>
                      </div>
                    ))
                  ) : displayNews.length > 0 ? displayNews.slice(0, 10).map((item, idx) => (
                    <Link 
                      key={item.id || idx} 
                      to={item.url ? item.url : `/news/article/${item.id}`} 
                      target={item.url ? '_blank' : '_self'}
                      rel={item.url ? 'noopener noreferrer' : undefined}
                      data-testid={`latest-news-${idx}`}
                      className="flex gap-4 mb-4 group"
                    >
                      <div className={cn("w-32 h-20 rounded-lg flex-shrink-0 overflow-hidden", isDark ? "bg-white/[0.04]" : "bg-gray-100")}>
                        <img 
                          src={item.image_url || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=200&h=150&fit=crop'} 
                          alt="" 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=200&h=150&fit=crop'; }}
                        />
                      </div>
                      <div>
                        <h4 className={cn("font-medium group-hover:text-purple-400 line-clamp-2", isDark ? "text-white" : "text-gray-900")}>{item.title}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          {item.source && <span className="text-xs text-purple-400">{item.source}</span>}
                          <p className="text-gray-500 text-xs">{item.published_at ? formatDate(item.published_at) : ''}</p>
                        </div>
                      </div>
                    </Link>
                  )) : (
                    <p className="text-gray-500 text-center py-4">No news available</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Right - Featured Article + Awards Promo */}
            <div className="lg:col-span-3 flex flex-col gap-4 sm:gap-6">
              <Card className={cn("overflow-hidden rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Featured</div>
                  <h4 className={cn("font-bold", isDark ? "text-white" : "text-gray-900")}>TradeFxP Regulation Status: Understanding an Unregulated Broker</h4>
                  <p className="text-gray-500 text-xs mt-2">{new Date().toLocaleDateString()}</p>
                </CardContent>
              </Card>

              {/* Awards 2026 banner */}
              <Link
                to="/awards"
                data-testid="awards-promo-card"
                className="block flex-1"
              >
                <Card
                  className={cn(
                    "overflow-hidden transition-colors cursor-pointer h-full border",
                    isDark ? "bg-transparent border-white/[0.06] hover:bg-white/[0.02]" : "bg-white border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <CardContent className="p-4 h-full flex flex-col justify-between">
                    <div>
                      <div className={cn("text-[10px] uppercase tracking-wider mb-2", isDark ? "text-gray-500" : "text-gray-500")}>
                        Forex Awards 2026
                      </div>
                      <h3 className={cn("text-base font-semibold mb-1", isDark ? "text-white" : "text-gray-900")}>Best Brokers of 2026</h3>
                      <p className={cn("text-sm", isDark ? "text-gray-400" : "text-gray-600")}>Vote for your favourite broker. Live community-driven rankings across 12 categories.</p>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-baseline gap-1.5">
                        <span className={cn("text-lg font-semibold", isDark ? "text-white" : "text-gray-900")}>12</span>
                        <span className={cn("text-xs", isDark ? "text-gray-500" : "text-gray-500")}>categories</span>
                      </div>
                      <span className="text-sm text-purple-400 inline-flex items-center">
                        Vote Now <ChevronRight className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Categories */}
      <section className={cn("py-6 border-t", isDark ? "border-white/[0.04]" : "border-gray-200/60")}>
        <div className="container-app">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {[
              { label: 'All Brokers', count: brokerStats ? `${brokerStats.total_brokers?.toLocaleString()}` : '5,491', icon: Building2, path: '/brokers', color: BRAND.purple },
              { label: '🇦🇺 Australia', sub: 'ASIC', path: '/brokers?country=Australia', color: BRAND.orange },
              { label: '🇬🇧 UK', sub: 'FCA', path: '/brokers?country=UK', color: BRAND.purpleMid },
              { label: '🇺🇸 USA', sub: 'NFA/CFTC', path: '/brokers?country=USA', color: BRAND.red },
              { label: '🇪🇺 EU', sub: 'CySEC', path: '/brokers?country=Cyprus', color: BRAND.green },
              { label: 'Expert Advisors', count: '150+', icon: Bot, path: '/toolbox?category=ea', badge: 'HOT' },
              { label: 'VPS Hosting', count: '$19/mo', icon: Server, path: '/toolbox?category=vps', badge: 'SALE' },
              { label: 'Copy Trading', sub: 'Top Traders', icon: Users, path: '/traders' },
              { label: 'Live Charts', icon: LineChart, path: '/charts' },
            ].map((cat, idx) => (
              <Link
                key={idx}
                to={cat.path}
                className={cn(
                  "group relative flex items-center gap-3 px-3 py-2 rounded-lg transition-colors shrink-0",
                  isDark 
                    ? "hover:bg-white/[0.04]"
                    : "hover:bg-gray-100"
                )}
              >
                {cat.badge && (
                  <span className="absolute -top-2 -right-1 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full" 
                    style={{ backgroundColor: cat.badge === 'HOT' ? BRAND.orange : BRAND.red }}>
                    {cat.badge}
                  </span>
                )}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                  style={{ backgroundColor: cat.color ? `${cat.color}18` : isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6' }}>
                  {cat.icon ? <cat.icon className="h-4 w-4" style={{ color: cat.color || '#888' }} /> : cat.label.split(' ')[0]}
                </div>
                <div>
                  <p className={cn("text-sm font-medium whitespace-nowrap", isDark ? "text-white" : "text-gray-900")}>{cat.icon ? cat.label : cat.label.split(' ').slice(1).join(' ')}</p>
                  <p className="text-xs text-gray-500">{cat.count || cat.sub}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Row */}
      <section className={cn("py-6 border-t", isDark ? "border-white/[0.04]" : "border-gray-200/60")} ref={statsRef}>
        <div className="container-app">
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16">
            {[
              { value: animatedStats.brokers, label: 'Brokers Listed', icon: Building2, suffix: '+' },
              { value: animatedStats.regulators, label: 'Global Regulators', icon: Shield, suffix: '+' },
              { value: animatedStats.traders, label: 'Active Traders', icon: Users, suffix: '+' },
              { value: animatedStats.reviews, label: 'User Reviews', icon: MessageSquare, format: true },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <stat.icon className="h-5 w-5 text-purple-400" />
                  <span className={cn("text-2xl lg:text-3xl font-bold tabular-nums", isDark ? "text-white" : "text-gray-900")}>
                    {stat.format ? formatNumber(stat.value) : stat.value.toLocaleString()}{stat.suffix || ''}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Content Grid */}
      <div className="container-app py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column - Main Content */}
          <div className="lg:col-span-8 space-y-6">

            {/* Market Sentiment & Trading Tools Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sentiment Indicators */}
              <Card className={cn("rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
                <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                  <div className="flex items-center justify-between">
                    <CardTitle className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                      Market Sentiment
                    </CardTitle>
                    <Badge className="bg-blue-500/20 text-blue-400 border-0 text-[9px]">Live Data</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {sentimentData.map((item, idx) => (
                    <div key={idx} data-testid={`sentiment-${item.pair}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className={isDark ? "text-gray-400" : "text-gray-600"}>{item.pair}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-green-400">{item.long}% Long</span>
                          <span className="text-red-400">{item.short}% Short</span>
                        </div>
                      </div>
                      <div className={cn("h-2 rounded-full flex overflow-hidden", isDark ? "bg-white/[0.06]" : "bg-gray-200")}>
                        <div className="bg-gradient-to-r from-green-500 to-green-400 transition-all" style={{ width: `${item.long}%` }} />
                        <div className="bg-gradient-to-r from-red-400 to-red-500 flex-1" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* VPS Promo + Signals Promo (stacked to fill the column) */}
              <div className="flex flex-col gap-4">
                <a 
                  href={VPS_AFFILIATE_LINK} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="vps-promo-card"
                  className="block"
                  onClick={() => trackVPSClick('homepage_vps_promo')}
                >
                  <Card className={cn("border overflow-hidden transition-colors cursor-pointer", isDark ? "bg-transparent border-white/[0.06] hover:bg-white/[0.02]" : "bg-white border-gray-200 hover:bg-gray-50")}>
                    <CardContent className="p-4">
                      <div className={cn("text-[10px] uppercase tracking-wider mb-2", "text-gray-500")}>Special Offer</div>
                      <h3 className={cn("text-base font-semibold mb-1", isDark ? "text-white" : "text-gray-900")}>Ultra-Fast Forex VPS</h3>
                      <p className={cn("text-sm mb-4", isDark ? "text-gray-400" : "text-gray-600")}>Low latency servers • 99.99% uptime</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={cn("text-lg font-semibold", isDark ? "text-white" : "text-gray-900")}>$19</span>
                          <span className="text-gray-500 text-sm">/mo</span>
                        </div>
                        <span className="text-sm text-orange-400 inline-flex items-center">
                          Get Started <ExternalLink className="h-4 w-4 ml-2" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </a>

                {/* Copy Top Traders banner → /signals marketplace */}
                <Link
                  to="/signals"
                  data-testid="signals-promo-card"
                  className="block flex-1"
                >
                  <Card className={cn("border overflow-hidden transition-colors cursor-pointer h-full", isDark ? "bg-transparent border-white/[0.06] hover:bg-white/[0.02]" : "bg-white border-gray-200 hover:bg-gray-50")}>
                    <CardContent className="p-4 h-full flex flex-col justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-2 text-gray-500">Copy Trading</div>
                        <h3 className={cn("text-base font-semibold mb-1", isDark ? "text-white" : "text-gray-900")}>Copy Top Traders</h3>
                        <p className={cn("text-sm", isDark ? "text-gray-400" : "text-gray-600")}>Auto-follow verified signal providers. MQL5-style marketplace.</p>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-lg font-semibold text-emerald-400">+18.4%</span>
                          <span className="text-gray-500 text-xs">avg 30d</span>
                        </div>
                        <span className="text-sm text-purple-400 inline-flex items-center">
                          Browse Signals <ChevronRight className="h-4 w-4 ml-1" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </div>

            {/* Courses (replaces Top Expert Advisors — links to free education library) */}
            <Card className={cn("overflow-hidden rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
              <CardHeader className={cn("py-3 px-4 border-b bg-gradient-to-r from-violet-500/10 to-transparent", isDark ? "border-white/[0.05]" : "border-gray-200/60")}>
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                    Courses
                  </CardTitle>
                  <Link to="/education" className="text-purple-400 text-xs hover:text-purple-300 flex items-center" data-testid="home-courses-view-all">
                    View All <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { id: 'm1', module: 'Module 1', title: 'Crypto Market Basics',                badge: 'BEGINNER' },
                    { id: 'm2', module: 'Module 2', title: 'Forex Foundations',                   badge: 'BEGINNER' },
                    { id: 'm3', module: 'Module 3', title: 'Risk Management & Backtesting',        badge: 'CORE' },
                    { id: 'm4', module: 'Module 4', title: 'Prop Firms — Trade With OPM',         badge: 'POPULAR' },
                  ].map((c) => (
                    <Link
                      key={c.id}
                      to="/education/course/free-trading-101"
                      data-testid={`home-course-${c.id}`}
                      className={cn(
                        "p-3 rounded-lg transition-colors flex flex-col justify-between min-h-[112px]",
                        isDark ? "bg-white/[0.03] hover:bg-white/[0.05]" : "bg-gray-50 hover:bg-gray-100"
                      )}
                    >
                      <div>
                        <div className={cn("text-[10px] font-bold uppercase tracking-wider mb-2", isDark ? "text-purple-300" : "text-purple-600")}>{c.module}</div>
                        <h4 className={cn("text-sm font-semibold leading-snug line-clamp-2", isDark ? "text-white" : "text-gray-900")}>{c.title}</h4>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] tracking-wider">FREE</Badge>
                        <span className="text-[10px] text-gray-500">{c.badge}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Latest News */}
            <Card className={cn("overflow-hidden rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
              <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                    Market News & Analysis
                  </CardTitle>
                  <Link to="/news" className="text-orange-400 text-xs hover:text-orange-300 flex items-center">
                    All News <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {deferredLoading ? (
                    [...Array(3)].map((_, idx) => (
                      <div key={idx} className={idx === 0 ? 'md:col-span-2' : ''}>
                        <div className={`flex gap-4 ${idx === 0 ? 'flex-row' : 'flex-col'}`}>
                          <div className={cn("rounded-lg animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200", idx === 0 ? 'w-48 h-28 shrink-0' : 'w-full h-32')} />
                          <div className="flex-1">
                            <div className={cn("w-full h-4 rounded mb-2 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                            <div className={cn("w-3/4 h-4 rounded mb-2 animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                            <div className={cn("w-20 h-3 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : news.slice(0, 4).map((article, idx) => (
                    <Link key={article.id} to={`/news/article/${article.id}`} 
                      className={`group ${idx === 0 ? 'md:col-span-2' : ''}`}>
                      <div className={`flex gap-4 ${idx === 0 ? 'flex-row' : 'flex-col'}`}>
                        <div className={cn(
                          "rounded-lg overflow-hidden",
                          isDark ? "bg-white/[0.04]" : "bg-gray-100",
                          idx === 0 ? 'w-48 h-28 shrink-0' : 'w-full h-32'
                        )}>
                          <img 
                            src={article.image_url || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop'} 
                            alt="" 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop'; }}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className={cn("font-medium group-hover:text-purple-400 transition-colors line-clamp-2 mb-2", isDark ? "text-white" : "text-gray-900")}>{article.title}</h4>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(article.published_at)}</span>
                            {article.source && <span>{article.source}</span>}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-4 space-y-5">
            
            {/* Premium CTA */}
            <Card className={cn("border", isDark ? "bg-transparent border-white/[0.06]" : "bg-white border-gray-200")}>
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-wider mb-2 text-gray-500">Limited Time</div>
                <h3 className={cn("text-base font-semibold mb-1", isDark ? "text-white" : "text-gray-900")}>50% OFF Premium</h3>
                <p className={cn("text-sm mb-4", isDark ? "text-gray-400" : "text-gray-600")}>Exclusive broker insights & advanced analytics</p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to="/register">Claim Offer</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Economic Calendar */}
            <Card className={cn("rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
              <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                    Economic Calendar
                  </CardTitle>
                  <Link to="/calendar"><ChevronRight className="h-4 w-4 text-gray-500 hover:text-purple-400" /></Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className={cn("divide-y", isDark ? "divide-white/[0.04]" : "divide-gray-100")}>
                  {deferredLoading ? (
                    [...Array(5)].map((_, idx) => (
                      <div key={idx} className="flex items-center gap-3 px-4 py-3">
                        <div className={cn("w-8 h-8 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className="flex-1">
                          <div className={cn("w-32 h-4 rounded animate-pulse mb-1", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          <div className={cn("w-16 h-3 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        </div>
                        <div className={cn("w-12 h-5 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                      </div>
                    ))
                  ) : events.slice(0, 5).map((event) => (
                    <div key={event.id} className={cn("flex items-center gap-3 px-4 py-3 transition-colors", isDark ? "hover:bg-white/[0.03]" : "hover:bg-gray-50")}>
                      <span className="text-xl">{getCurrencyFlag(event.currency)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-medium truncate", isDark ? "text-white" : "text-gray-900")}>{event.title}</p>
                        <p className="text-xs text-gray-500">{event.event_time}</p>
                      </div>
                      <Badge className={`text-[10px] px-1.5 py-0.5 ${
                        event.impact === 'high' ? 'bg-red-500/20 text-red-400' :
                        event.impact === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                        isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {event.impact}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Signal Providers */}
            <Card className={cn("rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
              <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                    Top Signal Providers
                  </CardTitle>
                  <Link to="/traders"><ChevronRight className="h-4 w-4 text-gray-500 hover:text-purple-400" /></Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className={cn("divide-y", isDark ? "divide-white/[0.04]" : "divide-gray-100")}>
                  {deferredLoading ? (
                    [...Array(5)].map((_, idx) => (
                      <div key={idx} className="flex items-center gap-3 px-4 py-3">
                        <div className={cn("w-8 h-8 rounded-full animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        <div className="flex-1">
                          <div className={cn("w-24 h-4 rounded animate-pulse mb-1", isDark ? "bg-gray-700" : "bg-gray-200")} />
                          <div className={cn("w-16 h-3 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                        </div>
                        <div className={cn("w-12 h-4 rounded animate-pulse", isDark ? "bg-gray-700" : "bg-gray-200")} />
                      </div>
                    ))
                  ) : traders.slice(0, 5).map((trader, idx) => (
                    <div key={trader.id} className={cn("flex items-center gap-3 px-4 py-3 transition-colors", isDark ? "hover:bg-white/[0.03]" : "hover:bg-gray-50")}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-semibold ${
                        idx === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                        idx === 1 ? 'bg-gradient-to-br from-violet-500 to-violet-700' :
                        'bg-gradient-to-br from-slate-500 to-slate-700'
                      }`}>
                        {trader.trader_name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-medium truncate", isDark ? "text-white" : "text-gray-900")}>{trader.trader_name}</p>
                        <p className="text-xs text-gray-500">{trader.followers?.toLocaleString()} followers</p>
                      </div>
                      <span className={`text-sm font-bold ${trader.performance_total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatPercentage(trader.performance_total)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={cn("p-3 border-t", isDark ? "border-white/[0.05]" : "border-gray-200/60")}>
                  <Button className="w-full h-8 text-xs" style={{ backgroundColor: BRAND.green }} asChild>
                    <Link to="/traders">Start Copy Trading</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Broker Warnings */}
            <Card className={cn("rounded-2xl", cn(isDark ? "bg-transparent border-white/[0.05]" : "bg-transparent border-gray-200/70"))}>
              <CardHeader className={cn("py-3 px-4 border-b", isDark ? "border-white/[0.05]" : "border-gray-200/50")}>
                <Link to="/warnings" className="block group" data-testid="home-recent-warnings-link">
                  <CardTitle className={cn("text-sm flex items-center justify-between", isDark ? "text-white" : "text-gray-900")}>
                    <span className="group-hover:text-rose-400 transition-colors">Recent Warnings</span>
                    <ChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-0.5 transition" />
                  </CardTitle>
                </Link>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {[
                  { name: 'FakeTrader Pro', issue: 'Unregulated', type: 'danger' },
                  { name: 'QuickFX Global', issue: 'Withdrawal issues', type: 'warning' },
                  { name: 'CryptoFX Prime', issue: 'Clone firm', type: 'danger' },
                ].map((item, idx) => (
                  <Link key={idx} to="/warnings" data-testid={`home-warning-${idx}`}
                    className={cn("flex items-center gap-3 p-2.5 rounded-lg transition-colors", isDark ? "hover:bg-white/[0.04]" : "hover:bg-gray-100")}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      item.type === 'danger' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                    }`}>
                      <FileWarning className={`h-4 w-4 ${item.type === 'danger' ? 'text-red-400' : 'text-yellow-400'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>{item.name}</p>
                      <p className={`text-xs ${item.type === 'danger' ? 'text-red-400' : 'text-yellow-400'}`}>{item.issue}</p>
                    </div>
                  </Link>
                ))}
                <Link to="/warnings" className="block text-center text-xs text-purple-400 hover:text-purple-300 pt-1" data-testid="home-view-all-warnings">
                  View All Warnings →
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <section className={cn("py-8 border-t", isDark ? "border-white/[0.05]" : "border-gray-200/60")}>
        <div className="container-app">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-wider mb-2 text-gray-500">For Brokers</div>
              <h2 className={cn("text-lg font-semibold mb-1", isDark ? "text-white" : "text-gray-900")}>List Your Brokerage on FX Astra</h2>
              <p className={cn("text-sm", isDark ? "text-gray-400" : "text-gray-600")}>Reach millions of traders worldwide with premium placement.</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" asChild>
                <Link to="/about">Learn More</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/register">Get Listed</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About FX Astra - Bottom Section */}
      <section className={cn("py-10 border-t", isDark ? "bg-transparent border-white/[0.05]" : "bg-white border-gray-200/60")}>
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8">
            <div className="flex-1">
              <h2 className={cn("text-xl font-bold mb-4", isDark ? "text-white" : "text-gray-900")}>What is FX Astra?</h2>
              <p className={cn("text-sm leading-relaxed max-w-2xl", isDark ? "text-gray-400" : "text-gray-600")}>
                FX Astra is the world&apos;s leading forex broker intelligence platform. We help traders research and compare {brokerStats?.total_brokers?.toLocaleString() || '5,491'}+ forex brokers, analyze spreads, read verified reviews, and make informed decisions. Our platform features real-time charts, economic calendar, copy trading signals, and comprehensive broker analysis tools.
              </p>
              <div className="flex flex-wrap gap-6 mt-6">
                <Link to="/about" className={cn("text-sm transition-colors", isDark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900")}>
                  Learn More
                </Link>
                <Link to="/brokers" className={cn("text-sm transition-colors", isDark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900")}>
                  Browse Brokers
                </Link>
                <Link to="/contact" className={cn("text-sm transition-colors", isDark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900")}>
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="text-xs text-gray-500 uppercase tracking-wider">Quick Stats</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center py-3">
                  <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{brokerStats?.total_brokers?.toLocaleString() || '5,491'}+</p>
                  <p className={cn("text-[10px] uppercase tracking-[0.14em] font-medium mt-0.5", isDark ? "text-gray-500" : "text-gray-500")}>Brokers</p>
                </div>
                <div className="text-center py-3">
                  <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>500+</p>
                  <p className={cn("text-[10px] uppercase tracking-[0.14em] font-medium mt-0.5", isDark ? "text-gray-500" : "text-gray-500")}>Traders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Animation Style */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
